import type { Allocation, Bed, Hostel, HostelFee, HostelMaintenance, Room, StudentOption, VisitorLog, WelfareCase } from "../../services/hostelService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Boarding's registers -- residences, rooms and beds, boarders, visitors,
 * repairs and welfare cases -- and the rules the screens explain. Choices
 * mirror apps/hostel/models.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const clock = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const money = (value?: string | number | null) => (value === null || value === undefined || value === "" ? "" : `UGX ${Math.round(Number(value)).toLocaleString()}`);
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const HOSTEL_TYPES: [string, string][] = [["boys", "Boys"], ["girls", "Girls"], ["mixed", "Mixed"]];
export const ROOM_TYPES: [string, string][] = [["dormitory", "Dormitory"], ["single", "Single"], ["double", "Double"], ["triple", "Triple"], ["quad", "Four-bed"]];
export const ROOM_CONDITIONS: [string, string][] = [["excellent", "Excellent"], ["good", "Good"], ["fair", "Fair"], ["poor", "Poor"], ["under_repair", "Being repaired"]];
export const BED_TYPES: [string, string][] = [["single", "Single"], ["bunk_bottom", "Bunk, bottom"], ["bunk_top", "Bunk, top"], ["double", "Double"]];
export const BED_CONDITIONS: [string, string][] = [["excellent", "Excellent"], ["good", "Good"], ["fair", "Fair"], ["poor", "Poor"], ["damaged", "Damaged"]];
export const FEE_TYPES: [string, string][] = [
  ["monthly", "Boarding fee"],
  ["security_deposit", "Security deposit"],
  ["maintenance", "Maintenance"],
  ["utilities", "Utilities"],
  ["laundry", "Laundry"],
  ["meal", "Meals"],
  ["other", "Other"],
];
export const VISITOR_TYPES: [string, string][] = [["parent", "Parent"], ["guardian", "Guardian"], ["relative", "Relative"], ["friend", "Friend"], ["official", "Official"], ["other", "Other"]];
export const VIOLATION_TYPES: [string, string][] = [
  ["curfew", "Late back / curfew"],
  ["noise", "Noise"],
  ["cleanliness", "Cleanliness"],
  ["visitors", "Visitors not allowed"],
  ["damage", "Damage to property"],
  ["fighting", "Fighting"],
  ["alcohol", "Alcohol or drugs"],
  ["other", "Other"],
];
export const SEVERITIES: [string, string][] = [["minor", "Minor"], ["moderate", "Moderate"], ["major", "Major"], ["severe", "Severe"]];
export const MAINTENANCE_TYPES: [string, string][] = [["repair", "Repair"], ["cleaning", "Cleaning"], ["routine", "Routine care"], ["inspection", "Inspection"], ["renovation", "Renovation"], ["emergency", "Emergency"]];
export const PRIORITIES: [string, string][] = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"]];
/** Kinds of work that take a room out of use (the room must be empty first); mirrors assign_maintenance. */
export const TAKES_ROOM_OFFLINE = ["repair", "renovation", "emergency"];

export const severityDot = (severity: string) => (severity === "severe" ? "bg-red-600" : severity === "major" ? "bg-red-500" : severity === "moderate" ? "bg-amber-500" : "bg-gray-400");
export const priorityDot = (priority: string) => (priority === "urgent" ? "bg-red-500" : priority === "high" ? "bg-amber-500" : priority === "medium" ? "bg-sky-500" : "bg-gray-400");

// --- Residences, rooms and beds --------------------------------------------------------------

export function hostelStatus(hostel: Pick<Hostel, "is_active" | "capacity" | "current_occupancy">) {
  if (!hostel.is_active) return { label: "Closed", dot: "bg-gray-400" };
  const free = hostel.capacity - hostel.current_occupancy;
  if (free <= 0) return { label: "Full", dot: "bg-red-500" };
  return { label: `${free} ${free === 1 ? "bed" : "beds"} free`, dot: "bg-emerald-500" };
}

/** Which learners a residence takes: boys, girls or both; mirrors the allocation serializer. */
export const suitsResidence = (learner: Pick<StudentOption, "gender">, hostel: Pick<Hostel, "hostel_type">) =>
  hostel.hostel_type === "mixed" || learner.gender === (hostel.hostel_type === "boys" ? "male" : "female");

export function bedStatus(bed: Pick<Bed, "is_occupied" | "is_reserved" | "is_under_maintenance" | "is_available" | "condition">) {
  if (bed.is_occupied) return { label: "Taken", dot: "bg-sky-500" };
  if (bed.is_reserved) return { label: "Held for a new boarder", dot: "bg-indigo-500" };
  if (bed.is_under_maintenance) return { label: "Out of use", dot: "bg-amber-500" };
  if (!bed.is_available) return { label: "Room not in use", dot: "bg-gray-400" };
  return { label: "Free", dot: "bg-emerald-500" };
}

export function roomStatus(room: Pick<Room, "is_active" | "condition" | "capacity" | "current_occupancy">) {
  if (!room.is_active) return { label: "Not in use", dot: "bg-gray-400" };
  if (room.condition === "under_repair") return { label: "Being repaired", dot: "bg-amber-500" };
  if (room.current_occupancy >= room.capacity) return { label: "Full", dot: "bg-sky-500" };
  return { label: `${room.capacity - room.current_occupancy} free`, dot: "bg-emerald-500" };
}

export const roomConfig: RegisterConfig<Room> = {
  search: (row) => [row.hostel_name, row.room_number, ...row.occupants.map((person) => person.name)].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "space", label: "Has a free bed", match: (row) => row.is_active && row.condition !== "under_repair" && row.current_occupancy < row.capacity },
    { key: "full", label: "Full", match: (row) => row.current_occupancy >= row.capacity },
    { key: "repair", label: "Being repaired", match: (row) => row.condition === "under_repair" },
  ],
  category: { label: "Residence", value: (row) => row.hostel_name },
  sorts: [
    { key: "room", label: "Residence, then room", compare: byText<Room>((row) => `${row.hostel_name} ${String(row.floor).padStart(3, "0")} ${row.room_number.padStart(6, "0")}`) },
    { key: "-free", label: "Most free beds", compare: reversed(byNumber<Room>((row) => row.capacity - row.current_occupancy)) },
  ],
};

/** The next bed label in a room: A, B, C… after the letters already used. */
export function nextBedLabel(beds: Pick<Bed, "bed_number">[]) {
  const used = new Set(beds.map((bed) => bed.bed_number.toUpperCase()));
  for (let index = 0; index < 26; index += 1) {
    const letter = String.fromCharCode(65 + index);
    if (!used.has(letter)) return letter;
  }
  return String(beds.length + 1);
}

// --- Boarders --------------------------------------------------------------------------------

export function allocationStatus(row: Pick<Allocation, "status">) {
  switch (row.status) {
    case "pending":
      return { label: "Waiting for approval", dot: "bg-amber-500" };
    case "active":
      return { label: "Boarding", dot: "bg-emerald-500" };
    case "suspended":
      return { label: "Suspended", dot: "bg-red-500" };
    case "completed":
      return { label: "Checked out", dot: "bg-gray-400" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const allocationConfig: RegisterConfig<Allocation> = {
  search: (row) => [row.student_name, row.student_id, row.hostel_name, row.room_number, row.emergency_contact_name].join(" "),
  views: [
    { key: "boarding", label: "Boarding", match: (row) => row.status === "active" },
    { key: "pending", label: "Waiting for approval", match: (row) => row.status === "pending" },
    { key: "ended", label: "Checked out or cancelled", match: (row) => row.status === "completed" || row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Start date", value: (row) => row.start_date },
  category: { label: "Residence", value: (row) => row.hostel_name },
  sorts: [
    { key: "learner", label: "Learner (A–Z)", compare: byText<Allocation>((row) => row.student_name) },
    { key: "room", label: "Residence and room", compare: byText<Allocation>((row) => `${row.hostel_name} ${row.room_number.padStart(6, "0")} ${row.bed_number}`) },
    { key: "-start", label: "Newest first", compare: reversed(byText<Allocation>((row) => `${row.start_date}${String(row.id).padStart(8, "0")}`)) },
  ],
};

/** The residence's current boarding fee, to fill in a new place. */
export function currentBoardingFee(fees: HostelFee[], hostel: number, today: string) {
  return fees.find((fee) => fee.hostel === hostel && fee.fee_type === "monthly" && fee.is_active && fee.effective_from <= today && (!fee.effective_until || fee.effective_until >= today));
}

// --- Visitors --------------------------------------------------------------------------------

export const isInside = (visit: Pick<VisitorLog, "visit_time_out" | "visit_date">, today: string) => !visit.visit_time_out && visit.visit_date === today;

export const visitorConfig = (today: string): RegisterConfig<VisitorLog> => ({
  search: (row) => [row.visitor_name, row.visitor_phone, row.student_name, row.student_id, row.purpose, row.hostel_name].join(" "),
  views: [
    { key: "inside", label: "Still inside", match: (row) => isInside(row, today) },
    { key: "today", label: "Today", match: (row) => row.visit_date === today },
    { key: "all", label: "All" },
  ],
  date: { label: "Visit date", value: (row) => row.visit_date },
  category: { label: "Residence", value: (row) => row.hostel_name },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<VisitorLog>((row) => `${row.visit_date}${row.visit_time_in}`)) },
    { key: "learner", label: "Boarder (A–Z)", compare: byText<VisitorLog>((row) => row.student_name) },
  ],
});

// --- Repairs ---------------------------------------------------------------------------------

export function maintenanceStatus(row: Pick<HostelMaintenance, "status">) {
  switch (row.status) {
    case "pending":
      return { label: "Waiting", dot: "bg-amber-500" };
    case "in_progress":
      return { label: "Being done", dot: "bg-sky-500" };
    case "completed":
      return { label: "Done", dot: "bg-emerald-500" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const maintenanceConfig: RegisterConfig<HostelMaintenance> = {
  search: (row) => [row.title, row.description, row.hostel_name, row.room_info, row.requested_by_name].join(" "),
  views: [
    { key: "open", label: "Open", match: (row) => row.status === "pending" || row.status === "in_progress" },
    { key: "done", label: "Done", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Asked for", value: (row) => row.requested_date },
  category: { label: "Residence", value: (row) => row.hostel_name },
  sorts: [
    {
      key: "urgent",
      label: "Most urgent first",
      compare: (a, b) =>
        Number(b.status === "pending" || b.status === "in_progress") - Number(a.status === "pending" || a.status === "in_progress") ||
        ["urgent", "high", "medium", "low"].indexOf(a.priority) - ["urgent", "high", "medium", "low"].indexOf(b.priority) ||
        b.requested_date.localeCompare(a.requested_date),
    },
    { key: "-when", label: "Newest first", compare: reversed(byText<HostelMaintenance>((row) => `${row.requested_date}${String(row.id).padStart(8, "0")}`)) },
  ],
};

// --- Welfare ---------------------------------------------------------------------------------

export function welfareStatus(row: Pick<WelfareCase, "is_resolved" | "action_taken" | "follow_up_required" | "follow_up_date">, today: string) {
  if (row.is_resolved) return { label: "Resolved", dot: "bg-emerald-500" };
  if (!row.action_taken) return { label: "Needs action", dot: "bg-red-500" };
  if (row.follow_up_required && row.follow_up_date && row.follow_up_date <= today) return { label: "Follow-up due", dot: "bg-amber-500" };
  return { label: "Action taken", dot: "bg-sky-500" };
}

export const welfareConfig = (today: string): RegisterConfig<WelfareCase> => ({
  search: (row) => [row.student_name, row.student_id, row.description, row.hostel_name, row.action_taken].join(" "),
  views: [
    { key: "open", label: "Open", match: (row) => !row.is_resolved },
    { key: "action", label: "Needs action", match: (row) => !row.is_resolved && !row.action_taken },
    { key: "follow-up", label: "Follow-up due", match: (row) => !row.is_resolved && row.follow_up_required && Boolean(row.follow_up_date) && row.follow_up_date! <= today },
    { key: "resolved", label: "Resolved", match: (row) => row.is_resolved },
    { key: "all", label: "All" },
  ],
  date: { label: "Date", value: (row) => row.incident_date },
  category: { label: "Kind", value: (row) => labelFrom(VIOLATION_TYPES, row.violation_type) },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<WelfareCase>((row) => `${row.incident_date}${row.incident_time}`)) },
    { key: "severity", label: "Most serious first", compare: (a, b) => SEVERITIES.findIndex(([key]) => key === b.severity) - SEVERITIES.findIndex(([key]) => key === a.severity) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<WelfareCase>((row) => row.student_name) },
  ],
});
